import {
  buildCctvRotationPopupHtml,
  buildPoolGridEntry,
  buildRotationSlides,
  filterManagementMonitoringCctvs,
  type MonitoringPopupPool,
  type PoolMonitoringCctv,
} from './monitoringPopupHtml';
import {
  buildCctvPoolGridPopupPath,
  buildCctvPoolRotationPopupPath,
  buildCctvPopupAbsoluteUrl,
} from './cctvPopupRoutes';
import {
  readMonitoringPoolGridRotationIntervalSec,
  readMonitoringRotationIntervalSec,
} from './monitoringRotationInterval';

export type PoolMonitoringPool = MonitoringPopupPool;

export type { PoolMonitoringCctv };

export type PoolMonitoringOpenOptions = {
  skipPurposeFilter?: boolean;
  useRemotePlayback?: boolean;
  siteGatewayEnabled?: boolean;
  rotationIntervalSec?: number;
  /** 순환 창 최초 표시 CCTV */
  initialCctvId?: number;
  /** true면 순환 일시정지(스톱) 상태로 진입 */
  startRotationPaused?: boolean;
  /** 전체화면(grid) 시설 순환 목록 (코드 순) */
  siblingPoolCodes?: string[];
  /** true면 grid 시설 순환 일시정지 상태로 진입 */
  startPoolRollingPaused?: boolean;
  /**
   * true(기본): 현재 창에서 전체화면 그리드로 이동 (새 창 부하 방지)
   * false: 예전처럼 새 창
   */
  sameWindow?: boolean;
  /** sameWindow 시 닫기 복귀 경로 (기본: 현재 location) */
  returnTo?: string | null;
  /** React Router navigate (있으면 SPA 이동, 없으면 location.assign) */
  navigate?: (to: string) => void;
};

export { filterManagementMonitoringCctvs };
export {
  readMonitoringRotationIntervalSec,
  writeMonitoringRotationIntervalSec,
  readMonitoringPoolGridRotationIntervalSec,
  writeMonitoringPoolGridRotationIntervalSec,
  DEFAULT_MONITORING_ROTATION_INTERVAL_SEC,
  DEFAULT_MONITORING_POOL_GRID_ROTATION_INTERVAL_SEC,
} from './monitoringRotationInterval';

type ScreenWithAvailOffset = Screen & { availLeft?: number; availTop?: number };

type PopupScreenRect = {
  left: number;
  top: number;
  w: number;
  h: number;
};

type MonitoringScreenDetailed = {
  left: number;
  top: number;
  width: number;
  height: number;
  availLeft: number;
  availTop: number;
  availWidth: number;
  availHeight: number;
  isPrimary: boolean;
  isInternal: boolean;
  label: string;
};

type MonitoringScreenDetails = {
  screens: MonitoringScreenDetailed[];
  currentScreen: MonitoringScreenDetailed;
};

type WindowWithScreenDetails = Window & {
  getScreenDetails?: () => Promise<MonitoringScreenDetails>;
};

function getCurrentScreenAvailGeometry(): PopupScreenRect {
  if (typeof window === 'undefined') {
    return { w: 1600, h: 900, left: 0, top: 0 };
  }
  const screen = window.screen as ScreenWithAvailOffset;
  return {
    w: screen.availWidth ?? 1600,
    h: screen.availHeight ?? 900,
    left: screen.availLeft ?? 0,
    top: screen.availTop ?? 0,
  };
}

function rectFromDetailedScreen(screen: MonitoringScreenDetailed): PopupScreenRect {
  return {
    left: screen.availLeft ?? screen.left,
    top: screen.availTop ?? screen.top,
    w: screen.availWidth ?? screen.width,
    h: screen.availHeight ?? screen.height,
  };
}

function chooseMonitoringTargetScreen(details: MonitoringScreenDetails): PopupScreenRect | null {
  const { screens, currentScreen } = details;
  if (!screens || screens.length <= 1) return null;

  const nonPrimary = screens.filter((s) => !s.isPrimary);
  if (nonPrimary.length > 0) {
    const best = [...nonPrimary].sort(
      (a, b) => b.availWidth * b.availHeight - a.availWidth * a.availHeight
    )[0];
    return rectFromDetailedScreen(best);
  }

  const different = screens.find(
    (s) => s.left !== currentScreen.left || s.top !== currentScreen.top
  );
  if (different) return rectFromDetailedScreen(different);

  return null;
}

function applyPopupScreenRect(popup: Window, rect: PopupScreenRect): void {
  try {
    popup.focus();
    popup.moveTo(rect.left, rect.top);
    popup.resizeTo(rect.w, rect.h);
  } catch {
    /* resizeTo/moveTo 제한 */
  }
}

export function maximizeMonitoringPopupWindow(popup: Window | null): void {
  if (!popup) return;
  applyPopupScreenRect(popup, getCurrentScreenAvailGeometry());
}

export async function placeMonitoringPopupOnPreferredScreen(
  popup: Window | null
): Promise<void> {
  if (!popup || popup.closed) return;

  const preferSecondary =
    localStorage.getItem('monitoringPopupPreferSecondary') !== 'false';

  if (preferSecondary && typeof window !== 'undefined') {
    const win = window as WindowWithScreenDetails;
    if (typeof win.getScreenDetails === 'function') {
      try {
        const details = await win.getScreenDetails();
        const target = chooseMonitoringTargetScreen(details);
        if (target) {
          applyPopupScreenRect(popup, target);
          return;
        }
      } catch (err) {
        console.warn('보조 모니터 배치 실패, 현재 화면에 맞춥니다:', err);
      }
    }
  }

  maximizeMonitoringPopupWindow(popup);
}

function getMonitoringPopupWindowFeaturesFromRect(rect: PopupScreenRect): string {
  return (
    'width=' +
    rect.w +
    ',height=' +
    rect.h +
    ',left=' +
    rect.left +
    ',top=' +
    rect.top +
    ',resizable=yes,scrollbars=yes'
  );
}

export function getMonitoringPopupWindowFeatures(): string {
  return getMonitoringPopupWindowFeaturesFromRect(getCurrentScreenAvailGeometry());
}

/** @deprecated use getMonitoringPopupWindowFeatures() */
export const MONITORING_POPUP_WINDOW_FEATURES = 'width=1600,resizable=yes';

export function openBlankMonitoringPopupWindow(): Window | null {
  return window.open('', '_blank', getMonitoringPopupWindowFeatures());
}

export function openMonitoringPopupAtPath(path: string): Window | null {
  const popup = window.open(
    buildCctvPopupAbsoluteUrl(path),
    '_blank',
    getMonitoringPopupWindowFeatures()
  );
  if (!popup) return null;
  focusMonitoringPopupWindow(popup);
  void placeMonitoringPopupOnPreferredScreen(popup).then(() => {
    focusMonitoringPopupWindow(popup);
  });
  return popup;
}

export function focusMonitoringPopupWindow(popup: Window | null): void {
  if (!popup || popup.closed) return;
  try {
    popup.focus();
  } catch {
    /* ignore */
  }
}

export function writeMonitoringPopupHtml(
  popup: Window,
  html: string,
  options?: { bringToFront?: boolean }
): void {
  popup.document.write(html);
  popup.document.close();
  if (options?.bringToFront !== false) {
    focusMonitoringPopupWindow(popup);
  }
  void placeMonitoringPopupOnPreferredScreen(popup).then(() => {
    if (options?.bringToFront !== false) {
      focusMonitoringPopupWindow(popup);
    }
  });
}

export function openPoolMonitoringInNewWindow(
  pool: MonitoringPopupPool,
  cctvs: PoolMonitoringCctv[],
  options?: PoolMonitoringOpenOptions
): boolean {
  const poolWithGw: MonitoringPopupPool = {
    ...pool,
    site_gateway_enabled:
      options?.siteGatewayEnabled ?? Boolean(pool.site_gateway_enabled),
  };

  const gridEntry = buildPoolGridEntry(poolWithGw, cctvs, {
    skipPurposeFilter: options?.skipPurposeFilter,
  });

  if (gridEntry.cctvs.length === 0) {
    alert('표시할 관제 CCTV가 없음');
    return false;
  }

  if (!pool.code) {
    alert('시설 코드가 없어 전체화면을 여기 불가');
    return false;
  }

  const sameWindow = options?.sameWindow !== false;
  const returnTo =
    options?.returnTo !== undefined
      ? options.returnTo
      : sameWindow && typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}`
        : null;

  const path = buildCctvPoolGridPopupPath(pool.code, {
    poolCodes: options?.siblingPoolCodes,
    intervalSec:
      options?.rotationIntervalSec ?? readMonitoringPoolGridRotationIntervalSec(),
    startPaused: options?.startPoolRollingPaused,
    inplace: sameWindow,
    returnTo: sameWindow ? returnTo : null,
  });

  if (sameWindow) {
    if (typeof options?.navigate === 'function') {
      options.navigate(path);
    } else if (typeof window !== 'undefined') {
      window.location.assign(buildCctvPopupAbsoluteUrl(path));
    }
    return true;
  }

  const newWindow = openMonitoringPopupAtPath(path);
  if (!newWindow) {
    alert('팝업 창을 여기 불가. 브라우저 설정에서 팝업 허용을 확인.');
    return false;
  }

  return true;
}

export function buildPoolRotationPopupHtmlByPoolId(
  pool: MonitoringPopupPool,
  cctvs: PoolMonitoringCctv[],
  useRemotePlayback: boolean,
  intervalSec: number,
  options?: { skipPurposeFilter?: boolean; siteGatewayEnabled?: boolean }
): string | null {
  const poolWithGw: MonitoringPopupPool = {
    ...pool,
    site_gateway_enabled:
      options?.siteGatewayEnabled ?? Boolean(pool.site_gateway_enabled),
  };
  const gridEntry = buildPoolGridEntry(poolWithGw, cctvs, {
    skipPurposeFilter: options?.skipPurposeFilter,
  });
  const slides = buildRotationSlides(gridEntry, useRemotePlayback);
  if (slides.length === 0) return null;
  const sec = Math.min(300, Math.max(1, Math.floor(intervalSec) || readMonitoringRotationIntervalSec()));
  return buildCctvRotationPopupHtml({
    title: `${pool.name} CCTV 순환`,
    poolName: pool.name,
    poolId: pool.id,
    slides,
    intervalSec: sec,
  });
}

export function commitMonitoringPopupHtml(popup: Window, html: string): void {
  writeMonitoringPopupHtml(popup, html, { bringToFront: true });
}

export function openPoolMonitoringRotationInNewWindow(
  pool: MonitoringPopupPool,
  cctvs: PoolMonitoringCctv[],
  options?: PoolMonitoringOpenOptions
): Window | null {
  const poolWithGw: MonitoringPopupPool = {
    ...pool,
    site_gateway_enabled:
      options?.siteGatewayEnabled ?? Boolean(pool.site_gateway_enabled),
  };
  const gridEntry = buildPoolGridEntry(poolWithGw, cctvs, {
    skipPurposeFilter: options?.skipPurposeFilter,
  });
  const slides = buildRotationSlides(gridEntry, Boolean(options?.useRemotePlayback));
  if (slides.length === 0) {
    alert('순환 재생할 CCTV 스트림 없음');
    return null;
  }

  if (!pool.code) {
    alert('시설 코드가 없어 순환 보기를 여기 불가');
    return null;
  }

  const sameWindow = options?.sameWindow !== false;
  const returnTo =
    options?.returnTo !== undefined
      ? options.returnTo
      : sameWindow && typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}`
        : null;

  const path = buildCctvPoolRotationPopupPath(pool.code, {
    intervalSec: options?.rotationIntervalSec ?? readMonitoringRotationIntervalSec(),
    cctvId: options?.initialCctvId,
    startPaused: options?.startRotationPaused,
    inplace: sameWindow,
    returnTo: sameWindow ? returnTo : null,
  });

  if (sameWindow) {
    if (typeof options?.navigate === 'function') {
      options.navigate(path);
    } else if (typeof window !== 'undefined') {
      window.location.assign(buildCctvPopupAbsoluteUrl(path));
    }
    return null;
  }

  const newWindow = openMonitoringPopupAtPath(path);
  if (!newWindow) {
    alert('팝업 창을 여기 불가. 브라우저 설정에서 팝업 허용을 확인.');
    return null;
  }

  return newWindow;
}

declare global {
  interface Window {
    __openCentralSinglePoolMonitoring?: (poolId: string) => void;
    __openCentralPoolRotationMonitoring?: (
      poolIdOrCode: string,
      intervalSec?: number,
      cctvId?: number,
      startPaused?: boolean
    ) => void;
    __getMonitoringPopupWindowFeatures?: () => string;
    __placeMonitoringPopup?: (popup: Window) => void;
  }
}

export {};
